import React, { forwardRef, useEffect, useImperativeHandle, useState, useMemo } from "react";
import classNames from "classnames";
import { Col, FormGroup, Input, Label, Row, UncontrolledTooltip } from "reactstrap";
import { formatPriceWithDollar } from "utils/currency";
import { formatDateForAPI, formatDateForPicker } from "utils/dateUtils";
import ReactDatetime from "components/CustomDatePicker";
import { useUpdateInventory } from "api/inventory/service";
import { useGetChannels } from "api/channels/service";
import ChannelsEmbedded from "views/settings/Channels/ChannelsEmbedded";
import { useTranslation } from 'react-i18next';
import Select from "components/Select";
import { getSelectStyles } from "utils/selectStyles";
import { getActiveColorStyle } from "utils/colorUtils";
import {
  getTheme2FormFieldsCss,
  getTheme2SearchFilterSingleSelectStyles,
} from "utils/theme2MultiSelect";
import InventoryRightDrawer from "../components/InventoryRightDrawer";

const PurchaseTab = forwardRef(
  ({ inventoryId, inventoryData, activeColor = "secondary", isTheme2 = false }, ref) => {
  const { t } = useTranslation();
  const updateMutation = useUpdateInventory();
  const { data: buyers } = useGetChannels("inventory_buyer");
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const [isLightTheme, setIsLightTheme] = useState(false);

  const [formData, setFormData] = useState({
    vehicle_cost: '',
    pack_fee: '',
    payment_method: '',
    seller_phone: '',
    where_acquired: '',
    acquired_date: '',
    payment_method: '',
    buyer: '',
    additional_notes: '',
    acquired_from: '',
    acquired_ref_number: '',
    acquired_from_name: '',
    acquired_from_name_channel: null,
    acquired_from_name_channel: null,
    acquired_from_name_customer: null,
    // Moved from VehicleTab
    primary_lot: '',
    stock_number: '',
    acquired_mileage: '',
    acquired_mileage_status: '',
    // Design additions
    year_previous_sale: '',
    calculus_id: '',
  });
  const paymentMethods = [
    { value: 'ach', label: t('purchaseTab.ach') },
    { value: 'ap', label: t('purchaseTab.ap') },
    { value: 'arm', label: t('purchaseTab.arm') },
    { value: 'cash', label: t('purchaseTab.cash') },
    { value: 'check', label: t('purchaseTab.check') },
    { value: 'draft', label: t('purchaseTab.draft') },
    { value: 'floor_plan', label: t('purchaseTab.floorPlan') },
    { value: 'repossession', label: t('purchaseTab.repossession') },
    { value: 'trade_in', label: t('purchaseTab.tradeIn') },
  ];
  const acquiredFromOptions = [
    { value: 'dealer', label: t('purchaseTab.dealer') },
    { value: 'individual', label: t('purchaseTab.individual') },
    { value: 'unknown', label: t('purchaseTab.unknown') },
  ];
  const [showAddBuyer, setShowAddBuyer] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      setIsLightTheme(document.body.classList.contains('white-content'));
    };
    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  const customSelectStyles = useMemo(() => getSelectStyles(activeColorStyle, isLightTheme), [activeColorStyle, isLightTheme]);
  const selectStyles = useMemo(
    () =>
      isTheme2
        ? getTheme2SearchFilterSingleSelectStyles(activeColorStyle, true)
        : customSelectStyles,
    [isTheme2, activeColorStyle, customSelectStyles]
  );
  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? getTheme2FormFieldsCss({
            scopeSelector: ".purchase-tab.purchase-tab--theme2",
            fieldsSelector: ".purchase-tab-fields",
          })
        : "",
    [isTheme2]
  );
  const inputClassName = isTheme2 ? "form-control" : undefined;
  const inputStyle = isTheme2
    ? { fontSize: "14px" }
    : { borderRadius: 0, fontSize: "14px" };
  const getDateInputProps = (extra = {}) => ({
    className: "form-control",
    style: isTheme2 ? { fontSize: "14px" } : { borderRadius: 0 },
    ...extra,
  });

  const handleInputChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBuyerChange = (value) => {
    if (value === "add_new_buyer") {
      setShowAddBuyer(true);
    } else {
      handleInputChange('buyer', String(value));
    }
  };

  useEffect(() => {
    if (inventoryData) {
      setFormData({
        vehicle_cost: formatPriceWithDollar(inventoryData?.inventory_pricing?.vehicle_cost) || 0,
        pack_fee: formatPriceWithDollar(inventoryData?.inventory_pricing?.pack_fee) || 0,
        payment_method: inventoryData?.inventory_pricing?.payment_method || '',
        seller_phone: inventoryData?.seller_phone || '',
        where_acquired: inventoryData?.where_acquired || '',
        acquired_date: formatDateForPicker(inventoryData?.acquired_date),
        payment_method: inventoryData?.inventory_pricing?.payment_method || '',
        buyer: inventoryData?.buyer?.id ? String(inventoryData.buyer.id) : '',
        additional_notes: inventoryData?.inventory_pricing?.additional_notes || '',
        acquired_from: inventoryData?.inventory_pricing?.acquired_from || '',
        acquired_ref_number: inventoryData?.inventory_pricing?.acquired_ref_number || '',
        acquired_from_name: inventoryData?.inventory_pricing?.acquired_from_name || '',
        acquired_from_name_channel: inventoryData?.inventory_pricing?.acquired_from_name_channel || null,
        acquired_from_name_customer: inventoryData?.inventory_pricing?.acquired_from_name_customer || null,
        // Moved from VehicleTab
        primary_lot: inventoryData?.location?.id || '',
        stock_number: inventoryData?.stock_number || '',
        acquired_mileage: inventoryData?.inventory_pricing?.acquired_mileage ?? '',
        acquired_mileage_status: inventoryData?.inventory_pricing?.acquired_mileage_status || '',
        // Design additions
        year_previous_sale: inventoryData?.inventory_vehicle?.year_previous_sale || '',
        calculus_id: inventoryData?.calculus_id || '',
      });
    }
  }, [inventoryData]);

  const handleSave = async () => {
    const toNumber = (v) => {
      if (v === '' || v === null || v === undefined) return 0;
      const str = v.toString();
      const cleaned = str.replace(/[^\d.-]/g, '');
      return cleaned ? Number(cleaned) : 0;
    };
    const updateData = {
      acquired_date: formatDateForAPI(formData.acquired_date),
      pricing: {
        vehicle_cost: toNumber(formData.vehicle_cost),
        pack_fee: toNumber(formData.pack_fee),
        payment_method: formData.payment_method,
        seller_phone: formData.seller_phone,
        where_acquired: formData.where_acquired,
        payment_method: formData.payment_method,
        additional_notes: formData.additional_notes,
        acquired_ref_number: formData.acquired_ref_number,
        acquired_from: formData.acquired_from,
        acquired_from_name: formData.acquired_from_name,
        acquired_from_name_channel: formData.acquired_from_name_channel,
        acquired_from_name_customer: formData.acquired_from_name_customer,
        acquired_mileage: formData.acquired_mileage === '' || formData.acquired_mileage == null
          ? null
          : toNumber(formData.acquired_mileage),
        acquired_mileage_status: formData.acquired_mileage_status || null,
      },
      ...(formData.buyer && { buyer: { id: formData.buyer } }),
      // Moved from VehicleTab
      location: {
        id: formData.primary_lot || null,
      },
      vehicle: {
        year_previous_sale: formData.year_previous_sale || null,
      }
    }

    updateMutation.mutate({ id: inventoryId, data: updateData });

    return { success: true };

  }

  useImperativeHandle(ref, () => ({
    handleSave
  }));

  const showValue = (value) => {
    return value === null || value === undefined || value === '' ? '' : value;
  };

  return (
    <div className={classNames("purchase-tab", { "purchase-tab--theme2": isTheme2 })}>
      {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
      {showAddBuyer &&
        <ChannelsEmbedded
          activeColor={activeColor}
          defaultType="inventory_buyer"
          onClose={() => setShowAddBuyer(false)}
          onCreated={(created) => {
            if (created?.id) {
              handleInputChange("buyer", String(created.id));
            } else if (created?.name) {
              handleInputChange("buyer", String(created.name));
            }
          }}
        />}
      {!showAddBuyer &&
        <div className={classNames("row", { "purchase-tab-fields": isTheme2 })}>
          {/* Purchase Information Card */}
          <div className="col-md-6 mb-2">
            <div className="card">
              <div className="card-header font-weight-bold text-uppercase">{t('purchaseTab.purchaseInformation')}</div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>{t('purchaseTab.purchaseDate')}</label>
                      <ReactDatetime
                        inputProps={getDateInputProps({
                          placeholder: t('purchaseTab.selectDate'),
                          maxLength: 10,
                        })}
                        value={formData.acquired_date}
                        onChange={(date) => handleInputChange('acquired_date', date)}
                        dateFormat="MM-DD-YYYY"
                        timeFormat={false}
                        closeOnSelect={true}
                        strictParsing={true}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <Label>{t('purchaseTab.vehicleCost')}</Label>
                      <Input
                        className={inputClassName}
                        style={inputStyle}
                        type="text"
                        value={formData.vehicle_cost}
                        onChange={(e) => handleInputChange('vehicle_cost', e.target.value)}
                        onBlur={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            vehicle_cost: formatPriceWithDollar(formData.vehicle_cost)
                          }));
                        }}
                        onFocus={(e) => {
                          const raw = formData.vehicle_cost.toString().replace(/[^\d.]/g, '');
                          setFormData(prev => ({ ...prev, vehicle_cost: raw }));
                        }}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>{t('purchaseTab.acquiredFrom')}</label>
                      <Select
                        styles={selectStyles}
                        className="react-select"
                        placeholder={t('purchaseTab.selectSource')}
                        name="acquired_from"
                        isSearchable={false}
                        closeMenuOnSelect={true}
                        value={formData.acquired_from ? {
                          value: formData.acquired_from,
                          label: acquiredFromOptions.find(opt => opt.value === formData.acquired_from)?.label || formData.acquired_from
                        } : null}
                        onChange={(option) => {
                          const newValue = option?.value || '';
                          handleInputChange('acquired_from', newValue);
                          // Reset selection details when type changes
                          handleInputChange('acquired_from_name', '');
                          handleInputChange('acquired_from_name_channel', null);
                          handleInputChange('acquired_from_name_customer', null);

                          if (newValue === 'dealer' || newValue === 'individual') {
                            setIsDrawerOpen(true);
                          }
                        }}
                        options={acquiredFromOptions}
                        isClearable
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>{t('purchaseTab.acquiredRefNumber')}</label>
                      <Input
                        className={inputClassName}
                        style={inputStyle}
                        type="text"
                        value={formData.acquired_ref_number}
                        onChange={(e) => handleInputChange('acquired_ref_number', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                {formData.acquired_from === 'dealer' && (
                  <div className="row">
                    <div className="col-md-12">
                      <div className="form-group">
                        <label>{t('purchaseTab.acquiredFromName')}</label>
                        <div
                          className="d-block font-weight-bold"
                          style={{ cursor: 'pointer', color: activeColorStyle.backgroundColor || '#007bff', fontSize: '14px' }}
                          onClick={() => setIsDrawerOpen(true)}
                        >
                          {formData.acquired_from_name_channel?.name || formData.acquired_from_name || t('purchaseTab.selectDealer')}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {formData.acquired_from === 'individual' && (
                  <div className="row">
                    <div className="col-md-12">
                      <div className="form-group">
                        <label>{t('purchaseTab.acquiredFromName')}</label>
                        <div
                          className="d-block font-weight-bold"
                          style={{ cursor: 'pointer', color: activeColorStyle.backgroundColor || '#007bff', fontSize: '14px' }}
                          onClick={() => setIsDrawerOpen(true)}
                        >
                          {formData.acquired_from_name_customer?.full_name || formData.acquired_from_name || t('purchaseTab.selectCustomer')}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <Row>

                  <Col md={6}>
                    <FormGroup>
                      <Label>{t('purchaseTab.paymentMethod')}</Label>
                      <Select
                        styles={selectStyles}
                        className="react-select"
                        placeholder={t('purchaseTab.selectMethod')}
                        name="payment_method"
                        isSearchable={false}
                        closeMenuOnSelect={true}
                        value={formData.payment_method ? {
                          value: formData.payment_method,
                          label: paymentMethods.find(method => method.value === formData.payment_method)?.label || formData.payment_method
                        } : null}
                        onChange={(option) => handleInputChange('payment_method', option?.value || '')}
                        options={paymentMethods}
                        isClearable
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>{t('purchaseTab.buyer')}</Label>
                      <Select
                        styles={selectStyles}
                        className="react-select"
                        placeholder={t('purchaseTab.selectBuyer')}
                        name="buyer"
                        isSearchable
                        closeMenuOnSelect={true}
                        value={formData.buyer ? {
                          value: formData.buyer,
                          label: formData.buyer === "add_new_buyer"
                            ? t('purchaseTab.addNewBuyer')
                            : buyers?.results?.find(buyer => String(buyer.id) === String(formData.buyer))?.name || formData.buyer
                        } : null}
                        onChange={(option) => {
                          if (option?.value === "add_new_buyer") {
                            handleBuyerChange("add_new_buyer");
                          } else {
                            handleBuyerChange(option?.value || "");
                          }
                        }}
                        options={[
                          { value: "add_new_buyer", label: t('purchaseTab.addNewBuyer') },
                          ...(buyers?.results?.map((buyer) => ({
                            value: String(buyer.id),
                            label: buyer.name
                          })) || [])
                        ]}
                        isClearable
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </div>
            </div>
          </div>
          <div className="col-md-6 mb-2">
            <div className="card">
              <div className="card-header font-weight-bold text-uppercase mb-3"> </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Vehicle Lot:</label>
                      <Select
                        styles={selectStyles}
                        className="react-select"
                        placeholder="Select Lot"
                        name="primary_lot"
                        value={formData.primary_lot ? {
                          value: formData.primary_lot,
                          label: inventoryData?.location?.name || formData.primary_lot
                        } : null}
                        onChange={(option) => handleInputChange('primary_lot', option?.value || '')}
                        options={[]} // Assuming global/parent handles these if needed, or fetched here
                        isClearable={false}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Stock #:</label>
                      <Input
                        className={inputClassName}
                        style={inputStyle}
                        type="text"
                        disabled={true}
                        value={formData.stock_number}
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <div className="d-flex align-items-center">
                        <label className="mb-0 mr-1">Acquired Mileage:</label>
                        <i className="fa fa-info-circle text-info" id="acquiredMileageTooltip" style={{ fontSize: '12px', cursor: 'pointer' }} />
                        <UncontrolledTooltip target="acquiredMileageTooltip" placement="top">
                          This is the actual mileage of the vehicle when it was originally purchased.
                        </UncontrolledTooltip>
                      </div>
                      <Input
                        className={inputClassName}
                        style={inputStyle}
                        type="text"
                        value={formData.acquired_mileage}
                        onChange={(e) => handleInputChange('acquired_mileage', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <div className="d-flex align-items-center">
                        <label className="mb-0 mr-1">Acquired Mileage Status:</label>
                        <i className="fa fa-info-circle text-info" id="acquiredMileageStatusTooltip" style={{ fontSize: '12px', cursor: 'pointer' }} />
                        <UncontrolledTooltip target="acquiredMileageStatusTooltip" placement="top">
                          This is the actual mileage status of the vehicle when it was originally purchased.
                        </UncontrolledTooltip>
                      </div>
                      <Select
                        styles={selectStyles}
                        className="react-select"
                        placeholder="Select Status"
                        value={formData.acquired_mileage_status ? {
                          value: formData.acquired_mileage_status,
                          label: [
                            { value: 'actual', label: 'Actual Miles' },
                            { value: 'unknown', label: 'True Mileage Unknown' },
                            { value: 'beyond_limits', label: 'Beyond Mechanical Limits' },
                            { value: 'exempt', label: 'Exempt' }
                          ].find(opt => opt.value === formData.acquired_mileage_status)?.label || formData.acquired_mileage_status
                        } : null}
                        onChange={(option) => handleInputChange('acquired_mileage_status', option?.value || '')}
                        options={[
                          { value: 'actual', label: 'Actual Miles' },
                          { value: 'unknown', label: 'True Mileage Unknown' },
                          { value: 'beyond_limits', label: 'Beyond Mechanical Limits' },
                          { value: 'exempt', label: 'Exempt' }
                        ]}
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>{t('purchaseTab.packFee')}</label>
                      <Input
                        className={inputClassName}
                        style={inputStyle}
                        type="text"
                        value={formData.pack_fee}
                        onChange={(e) => handleInputChange('pack_fee', e.target.value)}
                        onBlur={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            pack_fee: formatPriceWithDollar(formData.pack_fee)
                          }));
                        }}
                        onFocus={(e) => {
                          const raw = formData.pack_fee.toString().replace(/[^\d.]/g, '');
                          setFormData(prev => ({ ...prev, pack_fee: raw }));
                        }}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Year Previous Sale:</label>
                      <Input
                        className={inputClassName}
                        style={inputStyle}
                        type="text"
                        value={formData.year_previous_sale}
                        onChange={(e) => handleInputChange('year_previous_sale', e.target.value)}
                      />
                    </div>
                  </div>
                  {formData.calculus_id && (
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Repo Acct ID:</label>
                        <div className="font-weight-bold" style={{ fontSize: '14px' }}>
                          <a
                            href={`/accounts/${formData.calculus_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'none', fontWeight: '600' }}
                            onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                            onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                          >
                            {formData.calculus_id}
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-12 mb-4">
            <div className="card">
              <div className="card-body">
                <div className="form-group">
                  <label>{t('purchaseTab.additionalNotes')} (MAX 500 CHARACTERS)</label>
                  <textarea
                    style={inputStyle}
                    className="form-control"
                    maxLength={500}
                    rows={3}
                    value={showValue(formData.additional_notes)}
                    onChange={(e) => handleInputChange('additional_notes', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      }
      <InventoryRightDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        type={formData.acquired_from === 'dealer' ? 'dealer_wholesaler_auction' : formData.acquired_from}
        initialData={formData.acquired_from_name_customer}
        onSelect={(data) => {
          if (formData.acquired_from === 'individual') {
            handleInputChange('acquired_from_name', data.full_name);
            handleInputChange('acquired_from_name_customer', data.customer_id || data.id);
          } else {
            handleInputChange('acquired_from_name', data.name);
            handleInputChange('acquired_from_name_channel', data.id);
          }
        }}
        activeColorStyle={activeColorStyle}
        isLightTheme={isLightTheme}
      />
    </div>
  );
}
);

export default PurchaseTab; 